/**
 * Public changelog. Hand-curated from PROGRESS.md sprint deliverables — these
 * are the entries we'd actually want a customer or auditor to see, not every
 * internal commit. Newest first.
 *
 * When you ship something user-visible, add an entry here in the same shape
 * and bump the date. Keep entries short — the "why it matters" bullet is more
 * valuable than the implementation detail.
 */

type Entry = {
  date: string; // ISO yyyy-mm-dd
  version?: string;
  category: 'shipped' | 'fixed' | 'security' | 'performance';
  title: string;
  bullets: string[];
};

const ENTRIES: Entry[] = [
  {
    date: '2026-04-26',
    category: 'shipped',
    title: 'Postmark, Mailgun, and Amazon SES drivers',
    bullets: [
      'You can now connect Postmark, Mailgun (US/EU), or SES as a sending provider — not just Resend, SendGrid, and SMTP.',
      'Inbound webhook ingestion supports all three so opens/clicks/bounces flow into analytics automatically.',
      'SES uses a hand-rolled SigV4 signer so we did not bloat the runtime with the AWS SDK.',
    ],
  },
  {
    date: '2026-04-26',
    category: 'shipped',
    title: 'Live run history on automations',
    bullets: [
      'The advanced automation editor now shows the last 8 runs with status chips at the bottom of the canvas, polling every 5s.',
      'Backed by a new n8n callback endpoint with a per-run token — n8n reports completion so you can see if a workflow succeeded or failed without leaving Emailforge.',
    ],
  },
  {
    date: '2026-04-26',
    category: 'shipped',
    title: '95% credibility intervals on A/B experiments',
    bullets: [
      'Each variant now shows the 95% credibility interval on its conversion/open/click rate next to "Prob. best".',
      'A mini-bar visualizes overlap so you can tell at a glance whether the leader is actually separated from the runners-up or just noise.',
    ],
  },
  {
    date: '2026-04-26',
    category: 'shipped',
    title: 'Compliance documents are downloadable',
    bullets: [
      'Settings → Danger zone now has a one-click "Download" for the DPA, Privacy Policy, and Terms of Service.',
      'Each opens the page in a print-optimized layout so you can save it as PDF for your records or share with your DPO.',
    ],
  },
  {
    date: '2026-04-26',
    category: 'security',
    title: 'Tenant-isolation ESLint rule promoted to error',
    bullets: [
      'Our custom ESLint rule that flags Prisma queries missing organizationId in their where clause now fails CI instead of warning.',
      'Every legitimate exception in the codebase has been audited and annotated with a justification — new code that skips a tenant filter cannot ship.',
    ],
  },
  {
    date: '2026-04-25',
    category: 'shipped',
    title: 'NPS survey widget',
    bullets: [
      'Floating prompt in the corner of the dashboard with an 11-point NPS scale and follow-up textarea.',
      'Server-side 90-day cooldown plus a 7-day local "maybe later" so we never nag — and so we get fewer, more honest responses.',
    ],
  },
  {
    date: '2026-04-25',
    category: 'shipped',
    title: 'Cohort retention chart',
    bullets: [
      'Dashboard now plots weekly cohorts with 1-day, 7-day, and 30-day return rates, dedup by recipient.',
      'A heatmap-style gradient makes it obvious which weekly send produced the most engaged recipients.',
    ],
  },
  {
    date: '2026-04-25',
    category: 'shipped',
    title: 'Refresh tokens with rotation and theft detection',
    bullets: [
      'Sessions now use rotating refresh tokens — if one is reused after rotation we treat it as theft and revoke the entire chain, forcing re-auth on every device.',
      'OAuth (Google, GitHub, Discord), magic link, and password login all emit refresh tokens.',
      'The frontend transparently retries on 401 so users never see a logout when their access token expires.',
    ],
  },
  {
    date: '2026-04-25',
    category: 'shipped',
    title: 'Approval workflow in the editor',
    bullets: [
      'Templates can require a peer approval before publish. The editor has a third tab showing pending requests, history, and the approve/reject controls.',
      'The publish button is gated server-side too — you cannot bypass it from the API.',
    ],
  },
  {
    date: '2026-04-24',
    category: 'shipped',
    title: 'UTM preservation in click tracking',
    bullets: [
      'Click redirects now stamp utm_source=email, utm_medium=email, utm_campaign=<template-slug> when missing.',
      'Explicit UTMs in the template href always win — author intent beats the platform default.',
    ],
  },
  {
    date: '2026-04-24',
    category: 'shipped',
    title: 'Per-template tracking opt-out',
    bullets: [
      'You can now disable open or click tracking on individual templates from the editor toolbar — useful for transactional receipts where the privacy expectation is different from a marketing blast.',
    ],
  },
  {
    date: '2026-04-24',
    category: 'shipped',
    title: 'Webhook retries with exponential backoff',
    bullets: [
      'Outbound webhook deliveries that fail are retried after 1m, 5m, 30m, 2h, and 6h before giving up (~8.5h total).',
      'Each retry is a real BullMQ delayed job — no in-memory state to lose on restart.',
    ],
  },
  {
    date: '2026-04-24',
    category: 'shipped',
    title: 'Verified senders enforced in production',
    bullets: [
      'Sends from unverified from-addresses are now blocked in production with an actionable error pointing at the SPF/DKIM/DMARC instructions.',
      'In dev the check is off by default so you can iterate without the deliverability dance.',
    ],
  },
  {
    date: '2026-04-24',
    category: 'shipped',
    title: 'Tracking domains used in the render pass',
    bullets: [
      'Open pixels and click rewrites now route through your custom domain when one is configured and verified — improving deliverability and brand consistency.',
      'Falls back to the platform domain when none is set.',
    ],
  },
  {
    date: '2026-04-24',
    category: 'shipped',
    title: 'Onboarding wizard saves to the backend',
    bullets: [
      'The five-step wizard now persists each step: workspace name + subdomain, brand colors and fonts, template fork from the gallery, provider connection, and a real test send.',
      'You leave onboarding with a workspace you can actually use, not a UI that lied to you.',
    ],
  },
  {
    date: '2026-04-23',
    category: 'shipped',
    title: 'Custom domain provisioning',
    bullets: [
      'Add a tracking domain, get the CNAME, hit verify — Emailforge resolves it via DNS-over-HTTPS and toggles the status pill green when it propagates.',
    ],
  },
  {
    date: '2026-04-23',
    category: 'shipped',
    title: 'API keys and webhook endpoints',
    bullets: [
      'Generate scoped API keys (send:write, send:read, templates:read/write, webhooks:read) with one-time reveal and per-key rate limits.',
      'Configure webhook endpoints with HMAC signing — the dispatcher signs every payload Stripe-style and retries failures.',
    ],
  },
  {
    date: '2026-04-23',
    category: 'shipped',
    title: 'A/B testing with Bayesian P(best)',
    bullets: [
      'Multi-variant experiments with deterministic recipient assignment and Beta(1,1) priors.',
      'Auto-winner detection or manual declare; engagement events update stats in real time from provider webhooks.',
    ],
  },
  {
    date: '2026-04-22',
    category: 'shipped',
    title: 'Emailforge v1.0 — public preview',
    bullets: [
      'Drag-and-drop builder, 34 starter templates, MJML-style rendering, send pipeline with idempotency, multi-tenant from day one.',
      'OAuth (Google, GitHub, Discord), workspace branding, member roles, GDPR export and right-to-be-forgotten endpoints.',
    ],
  },
];

export function ChangelogPage() {
  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '48px 24px 80px' }}>
      <div
        className="mono"
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: 0.08,
          color: 'var(--flame-2)',
          marginBottom: 8,
        }}
      >
        Changelog
      </div>
      <h1 className="display" style={{ fontSize: 40, margin: '0 0 8px', letterSpacing: -0.02 }}>
        What's new
      </h1>
      <p style={{ fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.55, margin: 0 }}>
        We ship every weekday. Material changes — new features, security
        improvements, breaking-ish behavior — land here. Quiet bug fixes do
        not. Subscribe via the workspace's monthly billing email or the RSS
        feed (coming soon).
      </p>

      <div style={{ marginTop: 36, display: 'flex', flexDirection: 'column', gap: 28 }}>
        {ENTRIES.map((e, i) => (
          <ChangelogEntry key={i} entry={e} />
        ))}
      </div>
    </div>
  );
}

function ChangelogEntry({ entry }: { entry: Entry }) {
  return (
    <article
      className="mf-card"
      style={{
        padding: 20,
        background: 'var(--paper)',
        border: '1px solid var(--line)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span
          className="mono"
          style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: 0.05 }}
        >
          {formatDate(entry.date)}
        </span>
        <CategoryChip category={entry.category} />
        {entry.version && (
          <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            v{entry.version}
          </span>
        )}
      </div>
      <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 10px', letterSpacing: -0.01 }}>
        {entry.title}
      </h2>
      <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.6, color: 'var(--ink-2)' }}>
        {entry.bullets.map((b, i) => (
          <li key={i} style={{ marginBottom: 4 }}>
            {b}
          </li>
        ))}
      </ul>
    </article>
  );
}

function CategoryChip({ category }: { category: Entry['category'] }) {
  const meta: Record<Entry['category'], { label: string; color: string; bg: string }> = {
    shipped: { label: 'Shipped', color: 'var(--flame-2)', bg: 'var(--flame-soft)' },
    fixed: { label: 'Fixed', color: 'var(--ink-2)', bg: 'var(--paper-2)' },
    security: { label: 'Security', color: '#7a3500', bg: 'oklch(0.93 0.06 60)' },
    performance: { label: 'Performance', color: '#0a5e44', bg: 'oklch(0.93 0.06 165)' },
  };
  const m = meta[category];
  return (
    <span
      className="mono"
      style={{
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.08,
        padding: '2px 8px',
        borderRadius: 'var(--r-1)',
        color: m.color,
        background: m.bg,
      }}
    >
      {m.label}
    </span>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}
